<template>
    <div
        class="dv2-app"
        :class="{
            'dv2-app--collapsed': sidebarCollapsed && !isMobile,
            'dv2-app--dark': isDark,
            'dv2-app--branded': branding.isCustomized,
        }"
    >
        <header class="dv2-topbar">
            <div class="dv2-topbar__left">
                <button
                    type="button"
                    class="dv2-icon-btn"
                    :title="menuButtonTitle"
                    :aria-label="menuButtonTitle"
                    :aria-expanded="isMobile ? mobileOpen : !sidebarCollapsed"
                    @click="onMenuButtonClick"
                >
                    <span v-html="icons.menu"></span>
                </button>
                <router-link to="/dashboard" class="dv2-brand" :class="{ 'dv2-brand--custom': branding.isCustomized }">
                    <img
                        :src="brandLogoSrc"
                        class="dv2-brand__logo"
                        :alt="brandTitle"
                        @error="onBrandLogoError"
                    />
                    <span class="dv2-brand__text-wrap">
                        <span class="dv2-brand__text">{{ brandTitle }}</span>
                        <span v-if="branding.isCustomized" class="dv2-brand__platform">{{ branding.platformName }}</span>
                    </span>
                </router-link>
            </div>

            <div class="dv2-topbar__right">
                <button
                    type="button"
                    class="dv2-link-revert"
                    :disabled="prefs.saving"
                    @click="voltarAoLegado"
                >
                    Voltar para o visual antigo
                </button>
                <DashboardV2TopbarExtras />
            </div>
        </header>

        <div class="dv2-shell">
            <div
                v-if="mobileOpen"
                class="dv2-sidebar-overlay"
                @click="mobileOpen = false"
            ></div>

            <aside
                class="dv2-sidebar"
                :class="{
                    'is-open': mobileOpen,
                    'is-collapsed': sidebarCollapsed && !isMobile,
                }"
                aria-label="Navegação"
            >
                <nav class="dv2-nav">
                    <template v-for="entry in navItems" :key="entry.id">
                        <div
                            v-if="entry.type === 'group'"
                            class="dv2-nav__group"
                            :class="{
                                'is-open': isGroupOpen(entry.id),
                                'is-active': isGroupActive(entry),
                            }"
                            :data-dv2-group="entry.id"
                        >
                            <button
                                type="button"
                                class="dv2-nav__item dv2-nav__item--group"
                                :class="{ 'is-active': isGroupActive(entry) }"
                                :title="entry.label"
                                :aria-expanded="isGroupExpanded(entry.id)"
                                @click.stop="toggleGroup(entry)"
                            >
                                <span class="dv2-nav__icon" v-html="icons[entry.icon] || icons.more"></span>
                                <span class="dv2-nav__label">{{ entry.label }}</span>
                                <span
                                    class="dv2-nav__chevron"
                                    :class="{ 'is-open': isGroupExpanded(entry.id) }"
                                    v-html="icons.chevron"
                                ></span>
                            </button>

                            <div
                                v-show="isGroupOpen(entry.id) || (sidebarCollapsed && !isMobile && flyoutGroupId === entry.id)"
                                class="dv2-nav__submenu"
                                :class="{ 'is-flyout': sidebarCollapsed && !isMobile }"
                                :style="flyoutGroupId === entry.id ? flyoutPanelStyle : undefined"
                            >
                                <p v-if="sidebarCollapsed && !isMobile" class="dv2-nav__flyout-title">{{ entry.label }}</p>
                                <router-link
                                    v-for="child in entry.children"
                                    :key="child.id"
                                    :to="child.to"
                                    class="dv2-nav__subitem"
                                    :class="{ 'is-active': isActive(child) }"
                                    :title="child.label"
                                    :aria-current="isActive(child) ? 'page' : undefined"
                                    @click="onNavLinkClick"
                                >
                                    <span class="dv2-nav__subdot" aria-hidden="true"></span>
                                    <span>{{ child.label }}</span>
                                </router-link>
                            </div>
                        </div>

                        <a
                            v-else-if="entry.openInNewTab"
                            :href="entry.to"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="dv2-nav__item"
                            :title="entry.label"
                            @click="onNavLinkClick"
                        >
                            <span class="dv2-nav__icon" v-html="icons[entry.icon] || icons.home"></span>
                            <span class="dv2-nav__label">{{ entry.label }}</span>
                        </a>
                        <router-link
                            v-else
                            :to="entry.to"
                            class="dv2-nav__item"
                            :class="{ 'is-active': isActive(entry) }"
                            :title="entry.label"
                            :aria-current="isActive(entry) ? 'page' : undefined"
                            @click="onNavLinkClick"
                        >
                            <span class="dv2-nav__icon" v-html="icons[entry.icon] || icons.home"></span>
                            <span class="dv2-nav__label">{{ entry.label }}</span>
                        </router-link>
                    </template>
                </nav>

                <div class="dv2-sidebar__legal" aria-label="Termos, privacidade e contato">
                    <a
                        href="/termos-de-servico"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="dv2-sidebar__legal-link"
                        @click="onNavLinkClick"
                    >
                        Termos
                    </a>
                    <span class="dv2-sidebar__legal-sep" aria-hidden="true">•</span>
                    <a
                        href="/politica-de-privacidade"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="dv2-sidebar__legal-link"
                        @click="onNavLinkClick"
                    >
                        Privacidade
                    </a>
                    <span class="dv2-sidebar__legal-sep" aria-hidden="true">•</span>
                    <a
                        href="/ajuda?tab=contato"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="dv2-sidebar__legal-link"
                        @click="onNavLinkClick"
                    >
                        Contato
                    </a>
                </div>
            </aside>

            <div
                class="dv2-content"
                :class="{ 'dv2-content--home': isDashboardHome }"
            >
                <div id="breadcrumb" class="vue-portal-target dv2-breadcrumb"></div>
                <slot />
            </div>
        </div>

        <DashboardAssistantDock :hide-on-home="true" />
    </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useUserDashboardPreferencesStore } from '@/stores/userDashboardPreferences';
import { useSupportAssistantStore } from '@/stores/supportAssistant';
import { useBrandingStore } from '@/stores/branding';
import { useDashboardV2SidebarAutoCollapse } from '@/composables/useDashboardV2SidebarAutoCollapse';
import { switchToLegacyDashboardExperience } from '@/utils/dashboardV2Navigation';
import DashboardV2TopbarExtras from '@/components/dashboard-v2/DashboardV2TopbarExtras.vue';
import DashboardAssistantDock from '@/components/dashboard-v2/DashboardAssistantDock.vue';
import {
    dashboardV2Icons,
    resolveDashboardV2NavItems,
    isDashboardV2NavActive,
    isDashboardV2GroupActive,
} from '@/components/dashboard-v2/navConfig';
import { PLATFORM_LOGO_SRC } from '@/stores/branding';

const COLLAPSE_KEY = 'dv2_sidebar_collapsed';
const BRAND_LOGO_FALLBACK = PLATFORM_LOGO_SRC;

function readCollapsedPreference() {
    try {
        return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
        return false;
    }
}

const route = useRoute();
const router = useRouter();
const store = useStore();
const prefs = useUserDashboardPreferencesStore();
const branding = useBrandingStore();
const assistantStore = useSupportAssistantStore();
const mobileOpen = ref(false);
const brandLogoFailed = ref(false);
const flyoutPanelStyle = ref(null);
// Preferência lida no setup (antes do mount dos filhos) para o auto-collapse
// do preview/ajuda não ser sobrescrito pelo onMounted do shell.
const sidebarCollapsed = ref(readCollapsedPreference());
const flyoutGroupId = ref(null);
const openGroups = reactive({});
const viewportWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1200);

const icons = dashboardV2Icons;

const user = computed(() => store.state.auth?.user);
const isDark = computed(() => store.state.is_dark_mode);
const isMobile = computed(() => viewportWidth.value < 992);
const navItems = computed(() => resolveDashboardV2NavItems(user.value));
const brandTitle = computed(() => branding.chromeTitle);
const brandLogoSrc = computed(() => {
    if (brandLogoFailed.value) return BRAND_LOGO_FALLBACK;
    if (branding.logoUrl) return branding.logoUrl;
    return BRAND_LOGO_FALLBACK;
});

watch(
    () => branding.logoUrl,
    () => {
        brandLogoFailed.value = false;
    }
);

function onBrandLogoError() {
    brandLogoFailed.value = true;
}

const isDashboardHome = computed(() => {
    const name = route.name;
    const path = route.path;
    return (
        name === 'dashboard' ||
        name === 'dashboard-educador-digital' ||
        path === '/dashboard' ||
        path === '/dashboard-educador-digital'
    );
});

function pathIsDashboardHome(path) {
    return (
        path === '/dashboard' ||
        path === '/dashboard/' ||
        path === '/dashboard-educador-digital' ||
        path === '/dashboard-educador-digital/'
    );
}

watch(
    () => route.path,
    (toPath, fromPath) => {
        const leftHome = pathIsDashboardHome(fromPath) && !pathIsDashboardHome(toPath);
        if (leftHome && assistantStore.dashboardHasUserSession) {
            assistantStore.enableDashboardDock();
            assistantStore.minimizeDashboardDock();
            assistantStore.setDashboardDockUnread(true);
        }
        if (pathIsDashboardHome(toPath)) {
            assistantStore.minimizeDashboardDock();
        }
    }
);

const menuButtonTitle = computed(() => {
    if (isMobile.value) {
        return mobileOpen.value ? 'Fechar menu' : 'Abrir menu';
    }
    return sidebarCollapsed.value ? 'Expandir menu' : 'Recolher menu (só ícones)';
});

const { markSidebarAsUserChoice } = useDashboardV2SidebarAutoCollapse({
    sidebarCollapsed,
    mobileOpen,
    isMobile,
    flyoutGroupId,
});

function isActive(item) {
    return isDashboardV2NavActive(item, route.path);
}

function isGroupActive(group) {
    return isDashboardV2GroupActive(group, route.path);
}

function isGroupOpen(groupId) {
    if (sidebarCollapsed.value && !isMobile.value) {
        return false;
    }
    return !!openGroups[groupId];
}

/** Expandido no accordion (menu aberto) ou flyout ativo (menu só-ícones). */
function isGroupExpanded(groupId) {
    if (sidebarCollapsed.value && !isMobile.value) {
        return flyoutGroupId.value === groupId;
    }
    return isGroupOpen(groupId);
}

function syncOpenGroupsFromRoute() {
    for (const entry of navItems.value) {
        if (entry.type === 'group' && isGroupActive(entry)) {
            openGroups[entry.id] = true;
        }
    }
}

function clearFlyout() {
    flyoutGroupId.value = null;
    flyoutPanelStyle.value = null;
}

function positionCollapsedFlyout(groupId) {
    if (!sidebarCollapsed.value || isMobile.value || !groupId) {
        flyoutPanelStyle.value = null;
        return;
    }

    const trigger = document.querySelector(`[data-dv2-group="${groupId}"] .dv2-nav__item--group`);
    if (!trigger) {
        flyoutPanelStyle.value = null;
        return;
    }

    const rect = trigger.getBoundingClientRect();
    const gap = 8;
    const left = Math.min(rect.right + gap, window.innerWidth - 240);
    const viewportPad = 12;
    const spaceBelow = window.innerHeight - rect.top - viewportPad;
    const spaceAbove = rect.bottom - viewportPad;
    const openUp = spaceBelow < 300 && spaceAbove > spaceBelow;
    const maxHeight = Math.min(420, openUp ? spaceAbove : spaceBelow);

    if (openUp) {
        flyoutPanelStyle.value = {
            position: 'fixed',
            left: `${Math.max(viewportPad, left)}px`,
            top: 'auto',
            bottom: `${Math.max(viewportPad, window.innerHeight - rect.bottom)}px`,
            maxHeight: `${Math.max(160, maxHeight)}px`,
            zIndex: 120,
        };
    } else {
        flyoutPanelStyle.value = {
            position: 'fixed',
            left: `${Math.max(viewportPad, left)}px`,
            top: `${Math.max(viewportPad, rect.top)}px`,
            bottom: 'auto',
            maxHeight: `${Math.max(160, maxHeight)}px`,
            zIndex: 120,
        };
    }
}

async function openCollapsedFlyout(groupId) {
    flyoutGroupId.value = groupId;
    await nextTick();
    positionCollapsedFlyout(groupId);
}

function toggleGroup(entry) {
    if (sidebarCollapsed.value && !isMobile.value) {
        if (flyoutGroupId.value === entry.id) {
            clearFlyout();
            return;
        }
        openCollapsedFlyout(entry.id);
        return;
    }
    openGroups[entry.id] = !openGroups[entry.id];
    clearFlyout();
}

function onNavLinkClick() {
    mobileOpen.value = false;
    clearFlyout();
}

function onMenuButtonClick() {
    if (isMobile.value) {
        mobileOpen.value = !mobileOpen.value;
        markSidebarAsUserChoice();
        return;
    }
    sidebarCollapsed.value = !sidebarCollapsed.value;
    clearFlyout();
    markSidebarAsUserChoice();
    try {
        localStorage.setItem(COLLAPSE_KEY, sidebarCollapsed.value ? '1' : '0');
    } catch {
        /* ignore */
    }
}

function ensureNewHomeRoute() {
    if (route.name === 'Home' || route.path === '/') {
        router.replace({ name: 'dashboard' });
    }
}

function onResize() {
    viewportWidth.value = window.innerWidth;
    if (!isMobile.value) {
        mobileOpen.value = false;
    }
    if (flyoutGroupId.value) {
        positionCollapsedFlyout(flyoutGroupId.value);
    }
}

function onDocumentClick(event) {
    if (!sidebarCollapsed.value || isMobile.value || !flyoutGroupId.value) return;
    const target = event.target;
    if (target?.closest?.('.dv2-nav__group')) return;
    clearFlyout();
}

function onSystemThemeChange() {
    if ((store.state.dark_mode || 'light') === 'system') {
        store.commit('toggleDarkMode', 'system');
    }
}

watch(
    () => route.path,
    () => {
        syncOpenGroupsFromRoute();
        clearFlyout();
        // Preview / telas focadas: drawer mobile sempre fechado ao entrar
        if (route.name === 'documento-preview' || route.name === 'ajuda') {
            mobileOpen.value = false;
        }
    },
    { immediate: true }
);

watch(navItems, () => {
    syncOpenGroupsFromRoute();
});

let systemThemeMedia = null;

onMounted(() => {
    ensureNewHomeRoute();
    syncOpenGroupsFromRoute();
    window.addEventListener('resize', onResize);
    document.addEventListener('click', onDocumentClick);

    if (window.matchMedia) {
        systemThemeMedia = window.matchMedia('(prefers-color-scheme: dark)');
        if (systemThemeMedia.addEventListener) {
            systemThemeMedia.addEventListener('change', onSystemThemeChange);
        } else if (systemThemeMedia.addListener) {
            systemThemeMedia.addListener(onSystemThemeChange);
        }
    }
});

onUnmounted(() => {
    window.removeEventListener('resize', onResize);
    document.removeEventListener('click', onDocumentClick);
    if (systemThemeMedia) {
        if (systemThemeMedia.removeEventListener) {
            systemThemeMedia.removeEventListener('change', onSystemThemeChange);
        } else if (systemThemeMedia.removeListener) {
            systemThemeMedia.removeListener(onSystemThemeChange);
        }
    }
});

async function voltarAoLegado() {
    try {
        await switchToLegacyDashboardExperience(prefs, router);
    } catch {
        /* store */
    }
}
</script>

<style scoped>
.dv2-app {
    --dv2-bg: var(--brand-content-bg, var(--brand-body-bg, #f4f6fb));
    --dv2-surface: var(--brand-surface-bg, #ffffff);
    --dv2-surface-2: var(--brand-surface-item-bg, #f3f6fc);
    --dv2-navy: #1a2b4c;
    --dv2-muted: #7a8699;
    /* Co-branding: herda --brand-* do :root quando has-co-branding */
    --dv2-primary: var(--brand-primary, #2f6fed);
    --dv2-primary-soft: var(--brand-primary-soft, color-mix(in srgb, var(--brand-primary, #2f6fed) 14%, #ffffff));
    --dv2-border: color-mix(in srgb, var(--brand-primary, #2f6fed) 12%, #e6ebf3);
    --dv2-submenu-line: color-mix(in srgb, var(--brand-primary, #2f6fed) 10%, #e8eef8);
    --dv2-overlay: rgba(15, 23, 42, 0.35);
    --dv2-good: #16a34a;
    --dv2-good-bg: #eaf8ef;
    --dv2-warn: #d97706;
    --dv2-warn-bg: #fff4e5;
    --dv2-warn-text: #9a5b10;
    --dv2-chat-bot: var(--brand-surface-item-bg, #f8faff);
    --dv2-open-btn-bg: var(--brand-surface-item-bg, #fafbfe);
    --dv2-avatar-soft: var(--brand-primary-soft, #dbe7ff);
    --dv2-ghost-border: color-mix(in srgb, var(--brand-primary, #2f6fed) 28%, #c5d5f5);
    --dv2-shortcut-border: var(--dv2-border);
    --dv2-motivation: linear-gradient(
        135deg,
        color-mix(in srgb, var(--brand-primary, #2f6fed) 12%, #ffffff) 0%,
        color-mix(in srgb, var(--brand-accent, #7c3aed) 10%, #ffffff) 100%
    );
    --dv2-sidebar-w: 240px;
    --dv2-sidebar-w-collapsed: 72px;
    --dv2-radius: 16px;
    --dv2-shadow: 0 1px 2px rgba(26, 43, 76, 0.04), 0 8px 24px rgba(26, 43, 76, 0.04);
    --dv2-topbar-h: 56px;

    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background: var(--dv2-bg);
    color: var(--dv2-navy);
    font-family: 'Nunito', 'Segoe UI', sans-serif;
}

.dv2-app--dark {
    --dv2-bg: var(--brand-content-bg-dark, #0b1220);
    --dv2-surface: var(--brand-surface-bg-dark, #141c2c);
    --dv2-surface-2: var(--brand-surface-item-bg-dark, #1a2438);
    --dv2-navy: #e8eef9;
    --dv2-muted: #9aa8c0;
    --dv2-primary: var(--brand-primary, #6b9bff);
    --dv2-primary-soft: var(--dv2-primary-soft-dark, color-mix(in srgb, var(--brand-primary, #6b9bff) 22%, transparent));
    --dv2-border: color-mix(in srgb, var(--brand-primary, #6b9bff) 18%, #243149);
    --dv2-submenu-line: #2a3954;
    --dv2-overlay: rgba(0, 0, 0, 0.55);
    --dv2-good: #4ade80;
    --dv2-good-bg: rgba(74, 222, 128, 0.12);
    --dv2-warn: #fbbf24;
    --dv2-warn-bg: rgba(251, 191, 36, 0.12);
    --dv2-warn-text: #fcd34d;
    --dv2-chat-bot: var(--brand-surface-item-bg-dark, #1a2438);
    --dv2-open-btn-bg: var(--brand-surface-item-bg-dark, #1a2438);
    --dv2-avatar-soft: color-mix(in srgb, var(--brand-primary, #6b9bff) 22%, transparent);
    --dv2-ghost-border: color-mix(in srgb, var(--brand-primary, #6b9bff) 35%, #3a4d6e);
    --dv2-shortcut-border: var(--dv2-border);
    --dv2-motivation: linear-gradient(
        135deg,
        color-mix(in srgb, var(--brand-primary, #2f6fed) 25%, transparent) 0%,
        color-mix(in srgb, var(--brand-accent, #8b5cf6) 20%, transparent) 100%
    );
    --dv2-shadow: 0 1px 2px rgba(0, 0, 0, 0.2), 0 8px 24px rgba(0, 0, 0, 0.25);
    color-scheme: dark;
}

.dv2-topbar {
    height: var(--dv2-topbar-h);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 0 16px 0 10px;
    /* Com Identidade Visual: header = cor principal; senão surface do shell */
    background: var(--brand-header-bg, var(--dv2-surface));
    color: var(--brand-on-header, var(--dv2-navy));
    border-bottom: none;
    box-shadow: 0 1px 2px rgba(26, 43, 76, 0.03);
    position: sticky;
    top: 0;
    z-index: 40;
}

.dv2-app--dark .dv2-topbar {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
}

/* Contraste do chrome quando o topbar herda a cor principal */
.dv2-app--branded .dv2-topbar .dv2-brand__text,
.dv2-app--branded .dv2-topbar .dv2-brand--custom .dv2-brand__text {
    color: var(--brand-on-header, var(--dv2-navy));
}

.dv2-app--branded .dv2-topbar .dv2-brand__platform {
    color: var(--brand-on-header-muted, var(--dv2-muted));
}

.dv2-app--branded .dv2-topbar .dv2-icon-btn,
.dv2-app--branded .dv2-topbar .dv2-link-revert {
    color: var(--brand-on-header-muted, var(--dv2-muted));
}

.dv2-app--branded .dv2-topbar .dv2-icon-btn:hover,
.dv2-app--branded .dv2-topbar .dv2-link-revert:hover {
    color: var(--brand-on-header, var(--dv2-primary));
}

.dv2-topbar__left,
.dv2-topbar__right {
    display: flex;
    align-items: center;
    gap: 6px;
}

.dv2-brand {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
    color: inherit;
    min-width: 0;
}

.dv2-brand__logo {
    width: 28px;
    height: 28px;
    object-fit: contain;
    display: block;
    border-radius: 6px;
    flex-shrink: 0;
}

.dv2-brand__text-wrap {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
    line-height: 1.15;
}

.dv2-brand__text {
    font-size: 13px;
    color: var(--dv2-muted);
    font-weight: 700;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dv2-brand--custom .dv2-brand__text {
    color: var(--dv2-primary);
}

.dv2-brand__platform {
    display: block;
    font-size: 10px;
    font-weight: 500;
    color: var(--dv2-muted);
    opacity: 0.85;
    margin-top: 1px;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

@media (max-width: 991.98px) {
    .dv2-brand__text,
    .dv2-brand__platform {
        max-width: 140px;
    }
}

.dv2-link-revert {
    border: none;
    background: transparent;
    color: var(--dv2-muted);
    font-size: 11px;
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 3px;
    cursor: pointer;
    white-space: nowrap;
}

.dv2-link-revert:hover {
    color: var(--dv2-primary);
}

.dv2-icon-btn {
    width: 34px;
    height: 34px;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: var(--dv2-navy);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
}

.dv2-icon-btn:hover {
    background: transparent;
    color: var(--dv2-primary);
}

.dv2-shell {
    display: flex;
    flex: 1;
    min-height: 0;
    overflow: visible;
    position: relative;
}

.dv2-sidebar {
    width: var(--dv2-sidebar-w);
    flex-shrink: 0;
    /* Com Identidade Visual: tom mais claro da cor principal */
    background: var(--brand-sidebar-bg, var(--dv2-surface));
    border-right: 1px solid var(--dv2-border);
    display: flex;
    flex-direction: column;
    padding: 14px 10px 12px;
    position: sticky;
    top: var(--dv2-topbar-h, 56px);
    height: calc(100vh - var(--dv2-topbar-h, 56px));
    align-self: flex-start;
    z-index: 30;
    overflow: hidden;
    transition: width 0.2s ease;
}

.dv2-app--dark .dv2-sidebar {
    background: var(--brand-sidebar-bg-dark, var(--dv2-surface));
}

.dv2-sidebar.is-collapsed {
    width: var(--dv2-sidebar-w-collapsed);
    overflow: visible;
    /* Acima do .dv2-content para o flyout não ser coberto/cortado */
    z-index: 80;
}

.dv2-nav {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1 1 auto;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: rgba(122, 134, 153, 0.35) transparent;
}

/* Com menu só-ícones o flyout dos grupos sai para a direita;
   overflow no nav cortava o painel (regressão do scroll interno + legal). */
.dv2-sidebar.is-collapsed .dv2-nav {
    overflow: visible;
}

.dv2-nav::-webkit-scrollbar {
    width: 4px;
}

.dv2-nav::-webkit-scrollbar-track {
    background: transparent;
}

.dv2-nav::-webkit-scrollbar-thumb {
    background: rgba(122, 134, 153, 0.35);
    border-radius: 999px;
}

.dv2-nav::-webkit-scrollbar-thumb:hover {
    background: rgba(122, 134, 153, 0.55);
}

.dv2-sidebar__legal {
    flex: 0 0 auto;
    flex-shrink: 0;
    padding: 12px 8px 4px;
    text-align: center;
    line-height: 1.4;
    background: var(--brand-sidebar-bg, var(--dv2-surface));
    border-top: 1px solid color-mix(in srgb, var(--dv2-border) 35%, transparent);
    position: relative;
    z-index: 1;
}

.dv2-app--dark .dv2-sidebar__legal {
    background: var(--brand-sidebar-bg-dark, var(--dv2-surface));
}

.dv2-sidebar__legal-link {
    color: var(--dv2-muted);
    text-decoration: none;
    font-size: 11px;
    font-weight: 500;
    transition: color 0.15s ease;
}

.dv2-sidebar__legal-link:hover {
    color: var(--dv2-navy);
    text-decoration: none;
}

.dv2-sidebar__legal-sep {
    color: var(--dv2-border);
    font-size: 8px;
    margin: 0 4px;
}

.dv2-sidebar.is-collapsed .dv2-sidebar__legal {
    display: none;
}

.dv2-nav__group {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.dv2-nav__item {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    padding: 11px 12px;
    border-radius: 12px;
    color: var(--dv2-muted);
    text-decoration: none;
    font-size: 13px;
    font-weight: 650;
    transition: background 0.15s ease, color 0.15s ease;
    white-space: nowrap;
    border: none;
    background: transparent;
    width: 100%;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
}

.dv2-nav__item:hover {
    background: var(--dv2-surface-2);
    color: var(--dv2-navy);
}

.dv2-nav__item.is-active {
    background: var(--dv2-primary-soft);
    color: var(--dv2-primary);
}

.dv2-nav__item--group {
    justify-content: flex-start;
}

.dv2-nav__chevron {
    margin-left: auto;
    display: inline-flex;
    color: var(--dv2-muted);
    transition: transform 0.15s ease;
    flex-shrink: 0;
}

.dv2-nav__chevron.is-open {
    transform: rotate(180deg);
    color: var(--dv2-primary);
}

.dv2-nav__icon {
    display: inline-flex;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    align-items: center;
    justify-content: center;
}

.dv2-nav__submenu {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 2px 0 6px 12px;
    margin-left: 18px;
    border-left: 2px solid var(--dv2-submenu-line);
}

.dv2-nav__subitem {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-radius: 10px;
    color: var(--dv2-muted);
    text-decoration: none;
    font-size: 12.5px;
    font-weight: 600;
    line-height: 1.3;
}

.dv2-nav__subitem:hover {
    background: var(--dv2-surface-2);
    color: var(--dv2-navy);
}

.dv2-nav__subitem.is-active {
    background: var(--dv2-primary-soft);
    color: var(--dv2-primary);
}

.dv2-nav__subdot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.35;
    flex-shrink: 0;
}

.dv2-nav__subitem.is-active .dv2-nav__subdot {
    opacity: 1;
}

.dv2-nav__flyout-title {
    display: none;
}

.dv2-sidebar.is-collapsed .dv2-nav__item {
    justify-content: center;
    padding: 12px 8px;
}

.dv2-sidebar.is-collapsed .dv2-nav__label,
.dv2-sidebar.is-collapsed .dv2-nav__chevron {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.dv2-sidebar.is-collapsed .dv2-nav__submenu:not(.is-flyout) {
    display: none;
}

.dv2-sidebar.is-collapsed .dv2-nav__submenu.is-flyout {
    display: flex;
    position: absolute;
    left: calc(100% + 8px);
    top: 0;
    min-width: 220px;
    width: max-content;
    max-width: min(280px, calc(100vw - var(--dv2-sidebar-w-collapsed) - 24px));
    max-height: min(420px, calc(100vh - var(--dv2-topbar-h, 56px) - 24px));
    overflow-x: hidden;
    overflow-y: auto;
    margin: 0;
    padding: 8px;
    border: 1px solid var(--dv2-border);
    border-left: 1px solid var(--dv2-border);
    border-radius: 12px;
    background: var(--dv2-surface);
    box-shadow: 0 10px 28px rgba(26, 43, 76, 0.14);
    z-index: 90;
    scrollbar-width: thin;
}

.dv2-sidebar.is-collapsed .dv2-nav__flyout-title {
    display: block;
    margin: 0 0 6px;
    padding: 4px 8px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--dv2-muted);
}

.dv2-content {
    flex: 1;
    min-width: 0;
    padding: 6px 16px 28px;
    /* Sem z-index: overlays fixed das páginas (ex. .loader-overlay) não ficam
       presos abaixo do topbar/sidebar. Chrome do shell já usa z-index próprio. */
    position: relative;
}

.dv2-content--home {
    padding: 20px 24px 40px;
}

.dv2-breadcrumb:empty {
    display: none;
}

.dv2-breadcrumb:not(:empty) {
    display: flex;
    align-items: center;
    width: 100%;
    min-width: 0;
    margin-bottom: 4px;
}

.dv2-content--home .dv2-breadcrumb:not(:empty) {
    margin-bottom: 12px;
}

/* Espaçamentos Cork pensados para o header/toolbar antigos — compactar no shell v2
   (exceto o home do novo dashboard, que já tem composição própria) */
.dv2-content:not(.dv2-content--home) :deep(.layout-top-spacing) {
    margin-top: 4px !important;
}

.dv2-content:not(.dv2-content--home) :deep(.layout-px-spacing) {
    padding-left: 0 !important;
    padding-right: 0 !important;
    min-height: 0 !important;
}

.dv2-content:not(.dv2-content--home) :deep(.layout-spacing) {
    padding-bottom: 24px;
}

.dv2-content:not(.dv2-content--home) :deep(.breadcrumb-nav-wrap),
.dv2-content:not(.dv2-content--home) :deep(.page-header),
.dv2-content:not(.dv2-content--home) :deep(.navbar-nav.breadcrumb-nav-list) {
    margin-bottom: 0;
    padding-top: 0;
    padding-bottom: 0;
}

.dv2-content:not(.dv2-content--home) :deep(.main-content-toolbar) {
    min-height: 0;
    padding: 0;
    margin: 0;
}

.dv2-sidebar-overlay {
    display: none;
}

@media (max-width: 992px) {
    .dv2-brand__text,
    .dv2-link-revert {
        display: none;
    }

    .dv2-sidebar {
        position: fixed;
        left: 0;
        top: var(--dv2-topbar-h, 56px);
        width: min(300px, 88vw);
        transform: translateX(-105%);
        transition: transform 0.2s ease;
        box-shadow: 8px 0 24px rgba(26, 43, 76, 0.12);
        overflow-x: hidden;
    }

    .dv2-sidebar.is-collapsed {
        width: min(300px, 88vw);
        overflow-x: hidden;
    }

    .dv2-sidebar.is-open {
        transform: translateX(0);
    }

    .dv2-sidebar.is-collapsed .dv2-nav__label,
    .dv2-sidebar.is-collapsed .dv2-nav__chevron {
        position: static;
        width: auto;
        height: auto;
        margin: 0;
        clip: auto;
        overflow: visible;
    }

    .dv2-sidebar.is-collapsed .dv2-nav__item {
        justify-content: flex-start;
        padding: 11px 12px;
    }

    .dv2-sidebar.is-collapsed .dv2-nav__chevron {
        margin-left: auto;
    }

    .dv2-sidebar-overlay {
        display: block;
        position: fixed;
        inset: var(--dv2-topbar-h, 56px) 0 0 0;
        background: var(--dv2-overlay);
        z-index: 25;
    }

    .dv2-content {
        padding: 6px 12px 20px;
    }

    .dv2-content--home {
        padding: 16px;
    }
}
</style>
